import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiFehler } from "../api/client";
import {
  useBetrieb,
  useBetriebSpeichern,
  useInstallateurEinladen,
  useInstallateurSperren,
  useZugaengeSpeichern,
  useZugangslinkErneuern,
} from "../api/hooks";
import type { Betrieb, BetriebStammdaten, Einladung, NetzbetreiberZugang, PortalBenutzer } from "../api/typen";
import { Hinweis, Karte, Knopf, Modal, Spinner } from "../komponenten/Basis";
import { BetriebAuftritt } from "../komponenten/BetriebAuftritt";
import { BetriebLoeschenKnopf } from "../komponenten/Loeschen";
import { useAuth } from "../kontext/AuthKontext";
import { useToast } from "../kontext/ToastKontext";
import { datum, zeitpunkt } from "../lib/format";
import { kopiereText } from "../lib/zwischenablage";

export function BetriebSeite() {
  const { id } = useParams();
  const betriebId = Number(id);
  const abfrage = useBetrieb(betriebId);

  if (!Number.isInteger(betriebId) || betriebId <= 0) return <Hinweis art="fehler">Ungültige Betriebs-ID.</Hinweis>;
  if (abfrage.isLoading) return <div className="flex justify-center py-24"><Spinner label="Betrieb wird geladen…" /></div>;
  if (abfrage.error || !abfrage.data) return <Hinweis art="fehler">{abfrage.error?.message ?? "Betrieb nicht gefunden."}</Hinweis>;
  return <BetriebAnsicht betrieb={abfrage.data} />;
}

function BetriebAnsicht({ betrieb }: { betrieb: Betrieb }) {
  const speichern = useBetriebSpeichern(betrieb.id);
  const toast = useToast();

  function sperrenUmschalten() {
    const text = betrieb.aktiv
      ? `„${betrieb.name}“ sperren? Alle Mitarbeiter des Betriebs können sich dann nicht mehr anmelden.`
      : `„${betrieb.name}“ wieder freischalten?`;
    if (!window.confirm(text)) return;
    speichern.mutate(
      { aktiv: !betrieb.aktiv },
      {
        onSuccess: (b) => toast.info(b.aktiv ? "Betrieb freigeschaltet" : "Betrieb gesperrt", b.name),
        onError: (e) => toast.fehler(e, "Ändern fehlgeschlagen"),
      },
    );
  }

  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/betriebe" className="text-sm text-neutral-500 hover:text-neutral-900">← Alle Betriebe</Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
            {betrieb.name}
            {!betrieb.aktiv && <span className="rounded-full bg-neutral-200 px-2.5 py-0.5 text-xs font-semibold uppercase text-neutral-700">gesperrt</span>}
          </h1>
          <p className="mt-1 text-xs text-neutral-500">Kunde seit {datum(betrieb.created_at)} · {betrieb.kunden_anzahl} eingereichte Kunden</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Knopf variante={betrieb.aktiv ? "gefahr" : "sekundaer"} onClick={sperrenUmschalten} laedt={speichern.isPending}>
            {betrieb.aktiv ? "Betrieb sperren" : "Betrieb freischalten"}
          </Knopf>
          <BetriebLoeschenKnopf betrieb={betrieb} />
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="space-y-5">
          <StammdatenKarte betrieb={betrieb} />
          <BetriebAuftritt betrieb={betrieb} />
          <NetzbetreiberKarte betrieb={betrieb} />
        </div>
        <ZugaengeKarte betrieb={betrieb} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Stammdaten

const STAMMDATEN: { feld: keyof BetriebStammdaten; label: string; typ?: string; breit?: boolean }[] = [
  { feld: "name", label: "Firmenname", breit: true },
  { feld: "ansprechpartner", label: "Ansprechpartner" },
  { feld: "telefon", label: "Telefon", typ: "tel" },
  { feld: "email", label: "E-Mail (Firma)", typ: "email", breit: true },
  { feld: "strasse", label: "Straße und Hausnummer", breit: true },
  { feld: "plz", label: "PLZ" },
  { feld: "ort", label: "Ort" },
];

type Formular = Record<keyof BetriebStammdaten, string>;

function formAus(betrieb: Betrieb): Formular {
  const f = {} as Formular;
  for (const { feld } of [...STAMMDATEN, { feld: "notiz" as const }]) f[feld] = betrieb[feld] ?? "";
  return f;
}

function StammdatenKarte({ betrieb }: { betrieb: Betrieb }) {
  const speichern = useBetriebSpeichern(betrieb.id);
  const toast = useToast();
  const basis = useMemo(() => formAus(betrieb), [betrieb]);
  const [form, setForm] = useState(basis);
  useEffect(() => setForm(basis), [basis]);
  const geaendert = (Object.keys(basis) as (keyof Formular)[]).filter((k) => form[k].trim() !== basis[k].trim());

  function absenden(e: FormEvent) {
    e.preventDefault();
    if (!geaendert.length) return;
    if (form.plz.trim() && !/^\d{5}$/.test(form.plz.trim())) return toast.fehler(new Error("Die PLZ hat 5 Ziffern."), "Bitte prüfen");
    const body = Object.fromEntries(geaendert.map((k) => [k, form[k].trim()]));
    speichern.mutate(body, {
      onSuccess: () => toast.erfolg("Gespeichert", `${geaendert.length} Angabe(n) geändert`),
      onError: (err) => toast.fehler(err, "Speichern fehlgeschlagen"),
    });
  }

  const eingabe = "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm";
  return (
    <Karte className="p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500">Stammdaten</h2>
      <form onSubmit={absenden} className="grid gap-4 sm:grid-cols-2" noValidate>
        {STAMMDATEN.map(({ feld, label, typ, breit }) => (
          <div key={feld} className={breit ? "sm:col-span-2" : ""}>
            <label htmlFor={`betrieb-${feld}`} className="mb-1 block text-xs font-semibold">{label}</label>
            <input id={`betrieb-${feld}`} type={typ ?? "text"} className={eingabe} value={form[feld]} onChange={(e) => setForm({ ...form, [feld]: e.target.value })} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label htmlFor="betrieb-notiz" className="mb-1 block text-xs font-semibold">Interne Notiz <span className="font-normal text-neutral-400">(sieht der Betrieb nicht)</span></label>
          <textarea id="betrieb-notiz" rows={3} className={eingabe} value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} />
        </div>
        <div className="flex gap-2 sm:col-span-2">
          <Knopf variante="primaer" type="submit" disabled={!geaendert.length} laedt={speichern.isPending}>Speichern</Knopf>
          {geaendert.length > 0 && <Knopf variante="geist" onClick={() => setForm(basis)}>Verwerfen</Knopf>}
        </div>
      </form>
    </Karte>
  );
}

// ---------------------------------------------------------- Portal-Zugänge

function zugangStatus(b: PortalBenutzer): { text: string; klasse: string } {
  if (!b.aktiv) return { text: "gesperrt", klasse: "bg-neutral-200 text-neutral-700" };
  if (!b.aktiviert_am) {
    const abgelaufen = b.einladung_gueltig_bis && new Date(b.einladung_gueltig_bis) < new Date();
    return abgelaufen
      ? { text: "Einladung abgelaufen", klasse: "bg-ampel-rotHell text-ampel-rot" }
      : { text: `eingeladen, gültig bis ${datum(b.einladung_gueltig_bis)}`, klasse: "bg-ampel-gelbHell text-ampel-gelb" };
  }
  return { text: "aktiv", klasse: "bg-ampel-gruenHell text-ampel-gruen" };
}

function ZugaengeKarte({ betrieb }: { betrieb: Betrieb }) {
  const { benutzer } = useAuth();
  const istAdmin = benutzer?.rolle === "admin";
  const erneuern = useZugangslinkErneuern(betrieb.id);
  const sperren = useInstallateurSperren(betrieb.id);
  const toast = useToast();
  const [einladenOffen, setEinladenOffen] = useState(false);
  const [link, setLink] = useState<{ einladung: Einladung; neu: boolean } | null>(null);

  function neuerLink(b: PortalBenutzer) {
    const frage = b.aktiviert_am
      ? `Neuen Zugangslink für ${b.name} erstellen? Damit kann ${b.name} ein neues Passwort setzen (z. B. wenn es vergessen wurde).`
      : `Neuen Einladungslink für ${b.name} erstellen? Der bisherige Link wird ungültig.`;
    if (!window.confirm(frage)) return;
    erneuern.mutate(b.id, {
      onSuccess: (einladung) => setLink({ einladung, neu: false }),
      onError: (e) => toast.fehler(e, "Link konnte nicht erstellt werden"),
    });
  }

  function sperrenUmschalten(b: PortalBenutzer) {
    if (b.aktiv && !window.confirm(`Zugang von ${b.name} sperren? Anmelden ist danach nicht mehr möglich.`)) return;
    sperren.mutate(
      { benutzerId: b.id, aktiv: !b.aktiv },
      {
        onSuccess: (neu) => toast.info(neu.aktiv ? "Zugang entsperrt" : "Zugang gesperrt", neu.name),
        onError: (e) => toast.fehler(e, "Ändern fehlgeschlagen"),
      },
    );
  }

  return (
    <Karte className="p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Zugänge zum Portal</h2>
        {istAdmin && betrieb.aktiv && <Knopf variante="primaer" className="px-3 py-1.5 text-xs" onClick={() => setEinladenOffen(true)}>+ Mitarbeiter einladen</Knopf>}
      </div>
      {!istAdmin && <p className="mb-3 text-xs text-neutral-500">Zugänge anlegen, erneuern und sperren kann nur ein Admin.</p>}
      {betrieb.benutzer.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-300 p-4 text-sm text-neutral-500">
          Noch keine Zugänge. Lade den ersten Mitarbeiter ein – er bekommt einen Link, über den er sein Passwort festlegt.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {betrieb.benutzer.map((b) => {
            const status = zugangStatus(b);
            return (
              <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{b.name}</p>
                  <p className="truncate text-xs text-neutral-500">{b.email}</p>
                  <p className="mt-1 text-xs text-neutral-500">
                    <span className={`rounded px-1.5 py-0.5 font-semibold ${status.klasse}`}>{status.text}</span>
                    {b.letzter_login && <span className="ml-2">zuletzt angemeldet {zeitpunkt(b.letzter_login)}</span>}
                  </p>
                </div>
                {istAdmin && (
                  <div className="flex gap-2">
                    {b.aktiv && <Knopf className="px-3 py-1.5 text-xs" onClick={() => neuerLink(b)} laedt={erneuern.isPending && erneuern.variables === b.id}>{b.aktiviert_am ? "Passwort-Link" : "Neuer Link"}</Knopf>}
                    <Knopf variante={b.aktiv ? "geist" : "sekundaer"} className="px-3 py-1.5 text-xs" onClick={() => sperrenUmschalten(b)}>{b.aktiv ? "Sperren" : "Entsperren"}</Knopf>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {einladenOffen && (
        <EinladenModal betriebId={betrieb.id} schliessen={() => setEinladenOffen(false)} erstellt={(einladung) => setLink({ einladung, neu: true })} />
      )}
      {link && <LinkModal einladung={link.einladung} neu={link.neu} betrieb={betrieb.name} schliessen={() => setLink(null)} />}
    </Karte>
  );
}

function EinladenModal({ betriebId, schliessen, erstellt }: { betriebId: number; schliessen: () => void; erstellt: (e: Einladung) => void }) {
  const einladen = useInstallateurEinladen(betriebId);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (name.trim().length < 2) return setFehler("Bitte den Namen eingeben.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setFehler("Bitte eine gültige E-Mail-Adresse eingeben.");
    einladen.mutate(
      { name: name.trim(), email: email.trim() },
      {
        onSuccess: (einladung) => {
          schliessen();
          erstellt(einladung);
        },
        onError: (err) => setFehler(err instanceof ApiFehler ? err.message : "Einladen fehlgeschlagen"),
      },
    );
  }

  return (
    <Modal
      offen
      titel="Mitarbeiter einladen"
      beiSchliessen={schliessen}
      fuss={
        <>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf variante="primaer" type="submit" form="einladen" laedt={einladen.isPending}>Zugang anlegen</Knopf>
        </>
      }
    >
      <form id="einladen" onSubmit={absenden} className="space-y-4" noValidate>
        <div>
          <label htmlFor="einladen-name" className="mb-1 block text-xs font-semibold">Name</label>
          <input id="einladen-name" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} data-autofokus />
        </div>
        <div>
          <label htmlFor="einladen-email" className="mb-1 block text-xs font-semibold">E-Mail (Login)</label>
          <input id="einladen-email" type="email" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <p className="text-xs text-neutral-500">Danach bekommst du einen Einladungslink, den du dem Mitarbeiter selbst schickst (Mail, WhatsApp …).</p>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      </form>
    </Modal>
  );
}

function LinkModal({ einladung, neu, betrieb, schliessen }: { einladung: Einladung; neu: boolean; betrieb: string; schliessen: () => void }) {
  const toast = useToast();
  const b = einladung.benutzer;
  const nachricht =
    `Hallo ${b.name},\n\n` +
    (b.aktiviert_am
      ? `über diesen Link können Sie ein neues Passwort für Ihren AnmeldeMeister-Zugang (${betrieb}) festlegen:\n`
      : `Ihr Zugang zum AnmeldeMeister-Portal für ${betrieb} ist eingerichtet. Über diesen Link legen Sie Ihr Passwort fest:\n`) +
    `${einladung.link}\n\nDer Link ist bis ${datum(einladung.gueltig_bis)} gültig und funktioniert nur einmal. Ihr Login ist ${b.email}.`;

  async function kopieren(text: string, was: string) {
    if (await kopiereText(text)) toast.erfolg(`${was} kopiert`);
    else toast.fehler(new Error("Kopieren nicht möglich – bitte den Text markieren und selbst kopieren."));
  }

  return (
    <Modal offen titel={neu ? "Zugang angelegt" : "Neuer Link erstellt"} beiSchliessen={schliessen} fuss={<Knopf variante="primaer" onClick={schliessen}>Fertig</Knopf>}>
      <div className="space-y-4 text-sm">
        <Hinweis art="warnung">Der Link wird nur jetzt angezeigt. Bitte gleich an {b.name} schicken. Er ist bis {datum(einladung.gueltig_bis)} gültig und funktioniert nur einmal.</Hinweis>
        <div>
          <label htmlFor="einladungslink" className="mb-1 block text-xs font-semibold">Link</label>
          <div className="flex gap-2">
            <input id="einladungslink" readOnly className="w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 font-mono text-xs" value={einladung.link} onFocus={(e) => e.target.select()} />
            <Knopf onClick={() => void kopieren(einladung.link, "Link")} data-autofokus>Kopieren</Knopf>
          </div>
        </div>
        <div>
          <label htmlFor="einladungstext" className="mb-1 block text-xs font-semibold">Nachricht zum Weiterleiten</label>
          <textarea id="einladungstext" readOnly rows={7} className="w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-xs" value={nachricht} />
          <Knopf className="mt-2" onClick={() => void kopieren(nachricht, "Nachricht")}>Nachricht kopieren</Knopf>
        </div>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------- Netzbetreiber

function NetzbetreiberKarte({ betrieb }: { betrieb: Betrieb }) {
  const speichern = useZugaengeSpeichern(betrieb.id);
  const toast = useToast();
  const [zeilen, setZeilen] = useState<NetzbetreiberZugang[]>(betrieb.zugaenge);
  useEffect(() => setZeilen(betrieb.zugaenge), [betrieb.zugaenge]);
  const geaendert = JSON.stringify(zeilen) !== JSON.stringify(betrieb.zugaenge);

  function aendern(index: number, teil: Partial<NetzbetreiberZugang>) {
    setZeilen((alt) => alt.map((z, i) => (i === index ? { ...z, ...teil } : z)));
  }

  function sichern() {
    const bereinigt = zeilen.map((z) => ({ ...z, netzbetreiber: z.netzbetreiber.trim(), notiz: z.notiz?.trim() || null })).filter((z) => z.netzbetreiber);
    if (bereinigt.some((z) => z.netzbetreiber.length < 2)) return toast.fehler(new Error("Netzbetreiber-Namen brauchen mindestens 2 Zeichen."));
    speichern.mutate(bereinigt, {
      onSuccess: () => toast.erfolg("Netzbetreiber-Zugänge gespeichert"),
      onError: (e) => toast.fehler(e, "Speichern fehlgeschlagen"),
    });
  }

  return (
    <Karte className="p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Netzbetreiber-Portale</h2>
      <p className="mb-4 mt-1 text-xs text-neutral-500">
        Nur ob ein Zugang des Betriebs vorliegt. Die Zugangsdaten selbst gehören in deinen Passwortmanager, nie hierher.
      </p>
      {zeilen.length === 0 && <p className="mb-3 text-sm text-neutral-500">Noch keine Netzbetreiber eingetragen.</p>}
      <ul className="space-y-2">
        {zeilen.map((z, i) => (
          <li key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
            <input aria-label="Netzbetreiber" placeholder="z. B. RheinNetz GmbH" className="rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={z.netzbetreiber} onChange={(e) => aendern(i, { netzbetreiber: e.target.value })} />
            <input aria-label="Notiz" placeholder="Notiz (optional)" className="col-span-3 row-start-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm sm:col-span-1 sm:row-start-auto" value={z.notiz ?? ""} onChange={(e) => aendern(i, { notiz: e.target.value })} />
            <label className="flex items-center gap-1.5 whitespace-nowrap text-sm">
              <input type="checkbox" checked={z.zugang_vorhanden} onChange={(e) => aendern(i, { zugang_vorhanden: e.target.checked })} />
              Zugang da
            </label>
            <button type="button" aria-label="Zeile entfernen" className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" onClick={() => setZeilen(zeilen.filter((_, j) => j !== i))}>✕</button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <Knopf variante="geist" className="px-3 py-1.5 text-xs" onClick={() => setZeilen([...zeilen, { netzbetreiber: "", zugang_vorhanden: false, notiz: null }])}>+ Netzbetreiber</Knopf>
        <Knopf variante="primaer" className="px-3 py-1.5 text-xs" disabled={!geaendert} laedt={speichern.isPending} onClick={sichern}>Speichern</Knopf>
      </div>
    </Karte>
  );
}
